import { createSlice } from '@reduxjs/toolkit'
import { useSelector } from 'react-redux'
import { mapKeys, isEmpty, omit, isPlainObject, mapValues, pick } from 'lodash'
import { createComponent, updateComponentKeys, removeComponents, duplicateComponent } from '../components/componentSlice'
import { uniqueComponentKey, reverseUniqueComponentKey, getTemplatePathChanges } from '../../common/utility'
import { getPathsFromConditions, getPathsFromTemplate } from 'lib/utilities/form/utilities'

export const CONTROLLED_FIELDS = [
  'id',
  'position',
  'fields',
  'positions',
  'label',
  'placeholder',
  'interactable',
  'if',
  'linked_fields',
  'edit_on_page',
  'templatePaths',
  'paths'
]

const initialState = {
  checkbox: {},
  data: {},
  signature: {}
}

export function checkIncompleteDataField(template) {
  if (!template.template || isEmpty(template.template)) return true
  return false
}

export function checkIncompleteCheckboxField(template) {
  const editOnPage = template?.edit_on_page
  const inDropDown = template?.checkbox?.conditional_logic
  if (editOnPage) return !editOnPage.path || !editOnPage.value
  return isEmpty(inDropDown)
}

export function checkIncompleteField(type, template) {
  if (type === 'data') return checkIncompleteDataField(template)
  if (type === 'checkbox') return checkIncompleteCheckboxField(template)
  return false
} 

function addField(state, { key, component, template }) {
  if (component.type === 'data')
    state.data[component.id] = { ...template, id: component.id, position: key, }

  if (component.type === 'checkbox')
    state.checkbox[component.id] = { ...template, id: component.id, position: key, checkbox: {symbol: 'check', ...template?.checkbox} }

  return
}

function addSignature(state, { key, component, template }) {
  if (component.type !== 'signature' || !component.subtype)
    return

  state.signature[component.id] ||= {
    ...template,
    id: component.id,
    positions: {},
  }

  state.signature[component.id].positions[component.subtype] = key
}

function removeFromSignature(state, id, subtype) {
  if (!state.signature[id]) return

  if (subtype !== 'signature'){
    delete state.signature[id].positions[subtype]
  } else {
    delete state.signature[id]
  }
}

const mergeTemplatePaths = (oldPaths = [], removed, added) => {
  let updated = oldPaths.filter((e) =>
    e.type !== 'templatePath' || !removed.has(e.path)
  )
  for (let path of added) {
    updated.push({ type: 'templatePath', path })
  }
  return updated
}

const dataSlice = createSlice({
  name: 'data',
  initialState,
  reducers: {

    updateTemplate(state, { payload: { type, id, template}}) {
      if (!state[type][id]) return

      state[type][id] = { ...state[type][id], ...template }
    },

    updateTemplatePaths(state, { payload: { type, id, template } }) {
      if (!state[type][id]) return
      const preTemplate = pick(state[type][id], [...CONTROLLED_FIELDS, 'template', 'font_size', 'font_family'])

      if (!template.template || isEmpty(template.template)) {
        delete preTemplate.template
        delete preTemplate.templatePaths
        const newPaths = preTemplate?.paths?.filter((e) => e.type !== 'templatePath')
        state[type][id] = {
          ...preTemplate,
          ...template,
          paths: newPaths,
        }
        return
      }

      const prevPaths = getPathsFromTemplate(preTemplate.template)
      const newPaths = getPathsFromTemplate(template.template)
      const [removed, added] = getTemplatePathChanges(prevPaths, newPaths)

      const curPaths = preTemplate.paths || []
      const mergedPaths = (removed.size || added.size) ? mergeTemplatePaths(curPaths, removed, added) : curPaths

      state[type][id] = {
        ...preTemplate,
        ...template,
        templatePaths: newPaths,
        paths: mergedPaths,
      }
    },

    updateMultipleFieldsPath(state, { payload: { components, paths}}) {
      if (!components || !components.length) return
      components.forEach(({id, type}) => {
        if (!state[type][id]) return
        state[type][id] = { ...state[type][id], paths: paths }
      })
    },

    updateVisibility(state, { payload: { type, id, visibility}}) {
      if (!state[type][id]) return

      state[type][id] = { ...state[type][id], if: visibility }
    },

    updateMultipleVisibility(state, { payload: { components, visibility}}) {
      if (!components || !components.length) return
      components.forEach(({id, type}) => {
        if (!state[type][id]) return
        state[type][id] = { ...state[type][id], if: visibility }
      })
    },

    updateInteractability(state, { payload: { type, id, interactability}}) {
      if (!state[type][id]) return

      state[type][id] = { ...state[type][id], interactable: interactability }
    },

    updateMultipleInteractability(state, { payload: { components, interactability}}) {
      if (!components || !components.length) return
      components.forEach(({id, type}) => {
        if (!state[type][id]) return
        state[type][id] = { ...state[type][id], interactable: interactability }
      })
    },

    updateSymbol(state, { payload: { type, id, value}}) {
      if (!state[type][id]) return
      const checkbox = state[type][id].checkbox
      state[type][id] = { ...state[type][id], checkbox: {...checkbox, symbol: value} }
    },

    updateCheckboxConditionalLogic(state, { payload: { type, id, conditionalLogic}}) {
      if (!state[type][id]) return
      if (isEmpty(conditionalLogic) || !conditionalLogic) {
        const checkbox = state[type][id].checkbox
        delete checkbox.conditional_logic
        delete state[type][id].templatePaths
        state[type][id] = {
          ...state[type][id],
          checkbox: { ...checkbox },
          paths: []
        }
        return
      }
      const checkbox = state[type][id].checkbox

      const prevLogic = checkbox.conditional_logic || {}
      const prevPaths = getPathsFromConditions(prevLogic)
      const newPaths = getPathsFromConditions(conditionalLogic)

      const [removed, added] = getTemplatePathChanges(prevPaths, newPaths)
      const curPaths = state[type][id].paths || []
      const mergedPaths = removed.size || added.size ? mergeTemplatePaths(curPaths, removed, added) : curPaths

      state[type][id] = {
        ...state[type][id],
        checkbox: { ...checkbox, conditional_logic: conditionalLogic },
        templatePaths: newPaths,
        paths: mergedPaths,
      }
    },

    updateEditOnPage(state, { payload: { type, id, editOnPage}}) {
      if (!state[type][id]) return
      if (isEmpty(editOnPage) || !editOnPage) {
        delete state[type][id].edit_on_page
      } else {
        state[type][id] = { ...state[type][id], edit_on_page: editOnPage }
      }
    },

    loadTemplates(state, { payload: { type, templates }}) {
      state[type] = templates
    },

    updateTemplates(state, { payload: { type, templates }}) {
      state[type] ||= {}
      state[type] = { ...state[type], ...templates }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(createComponent, (state, { payload: { key, component }}) => {
      addField(state, { key, component })
      addSignature(state, { key, component })
    })

    builder.addCase(duplicateComponent, (state, { payload: { key, component, duplicateComponent }}) => {
      const template = state[component.type][component.id]
      addField(state, { key, component: duplicateComponent, template })
      addSignature(state, { key, component: duplicateComponent, template })
    })

    builder.addCase(updateComponentKeys, (state, { payload: { deltaIds, type }}) => {
      state[type] ||= {}

      let temp, newId
      for (let oldId in deltaIds) {
        newId = deltaIds[oldId]
        temp = state[type][oldId]
        delete state[type][oldId]

        if (isPlainObject(temp?.positions))
          state[type][newId] = { ...temp, id: newId, positions: mapValues(temp.positions, (_, subtype) => uniqueComponentKey(type, newId, subtype))  }
        if (temp?.position)
          state[type][newId] = { ...temp, id: newId, position: uniqueComponentKey(type, newId) }
      }
    })

    builder.addCase(removeComponents, (state, {payload: keys}) => {
      keys.forEach(key => {
        const { type, id, subtype } = reverseUniqueComponentKey(key)

        switch(type) {
        case 'data':
          delete state.data[id]
        case 'checkbox':
          delete state.checkbox[id]
          return
        case 'signature':
          removeFromSignature(state, id, subtype)
          return
        }
      })
    })
  }
})

export function useDataTemplate(type, id) {
  const data = useSelector(state => state.data[type][id])
  return data
}

export const { updateTemplate, updateTemplatePaths, updateMultipleFieldsPath, updateVisibility, updateMultipleVisibility, updateInteractability, updateMultipleInteractability, updateTemplates, updateSymbol, updateCheckboxConditionalLogic, updateEditOnPage, loadTemplates } = dataSlice.actions
export default dataSlice.reducer
